/**
 * Phase 17 Plan 17-02 — ReactivateKpiDialog
 *
 * Simple confirmation dialog to reactivate a deactivated KPI.
 */

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useReactivateKpiMutation } from '@/hooks/useKpiQueries';
import { toast } from '@/lib/toast';

export interface ReactivateKpiDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly workOrderId: number;
  readonly kpiId: number;
  readonly kpiName: string;
}

export function ReactivateKpiDialog({
  open,
  onOpenChange,
  workOrderId,
  kpiId,
  kpiName,
}: ReactivateKpiDialogProps): JSX.Element {
  const mutation = useReactivateKpiMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  function handleClose(): void {
    setServerError(null);
    mutation.reset();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    setServerError(null);
    mutation.mutate(
      { woId: workOrderId, kpiId },
      {
        onSuccess: () => {
          toast.success(`KPI "${kpiName}" reactivated`);
          handleClose();
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setServerError(msg);
          toast.error('Failed to reactivate KPI', { description: msg });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="reactivate-kpi-dialog">
        <DialogHeader>
          <DialogTitle>Reactivate KPI</DialogTitle>
          <DialogDescription>
            Reactivate <strong>{kpiName}</strong>? It will appear in new logsheet entries again.
          </DialogDescription>
        </DialogHeader>

        {serverError !== null && (
          <div
            className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
            data-testid="reactivate-kpi-error"
          >
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="reactivate-kpi-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            data-testid="reactivate-kpi-confirm"
          >
            {mutation.isPending ? 'Reactivating…' : 'Reactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
